/**
 * Date formatting utilities for grid components
 */
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { parseISO, isValid } from 'date-fns';

export const GridDateTimeMode = {
  Utc: 'utc',
  Local: 'local',
} as const;

export type GridDateTimeMode = (typeof GridDateTimeMode)[keyof typeof GridDateTimeMode];

export const getGridDateTimeMode = (settings?: string | null): GridDateTimeMode =>
  settings?.trim().toLowerCase() === 'true' ? GridDateTimeMode.Utc : GridDateTimeMode.Local;

const hasTimeZoneSuffix = (value: string): boolean => /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);

/** Parse grid datetime values as UTC when the backend omits a timezone suffix. */
export const parseBackendDateTime = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  const raw = String(value).trim();
  const normalized = hasTimeZoneSuffix(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toLocalDateFromUtcParts = (date: Date): Date =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );

const toUtcISOStringFromLocalParts = (date: Date): string =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    )
  ).toISOString();

/**
 * Format date/datetime values for input components
 * Converts ISO strings to Date objects for date pickers, or returns as strings
 *
 * @param value - Raw value from backend (ISO string, Date, or other)
 * @param fieldType - Type of field ('date', 'datetime', or 'other')
 * @returns Formatted value suitable for the input type
 */
export const formatDateTimeForInput = (
  value: unknown,
  fieldType: 'date' | 'datetime' | 'other',
  dateTimeMode?: GridDateTimeMode
): Date | string | null => {
  if (!value) {
    return fieldType === 'date' || fieldType === 'datetime' ? null : '';
  }
  const strValue = String(value);

  if (fieldType === 'date' || fieldType === 'datetime') {
    if (fieldType === 'datetime' && dateTimeMode) {
      const date = parseBackendDateTime(strValue);
      if (!date) return null;

      if (dateTimeMode === GridDateTimeMode.Utc) {
        return toLocalDateFromUtcParts(date);
      }

      return date;
    }

    const date = dayjs(strValue);
    return date.isValid() ? date.toDate() : null;
  }

  return strValue;
};

/** Normalize an API date/date-time to the value expected by an HTML date input. */
export const toDateInputValue = (value: unknown): string => {
  if (value == null) return '';

  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? `${yearText}-${monthText}-${dayText}`
    : '';
};

/**
 * Format ISO datetime string for display
 * Converts ISO format to user-friendly MM/DD/YYYY HH:MM format
 *
 * @param value - ISO datetime string (e.g., "2026-01-27T10:59:00.000Z")
 * @returns Formatted string (e.g., "01/27/2026 10:59")
 *
 * @example
 * formatDateTimeForDisplay("2026-01-27T10:59:00.000Z") // "01/27/2026 10:59"
 */
export const formatDateTimeForDisplay = (value: string): string => {
  if (!value) return '';

  const dateStr = String(value);
  if (dateStr.includes('T')) {
    const [datePart, timeWithZone] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    const timePart = timeWithZone.split('.')[0].substring(0, 5); // Get HH:MM
    return `${month}/${day}/${year} ${timePart}`;
  }

  return value;
};

/**
 * Convert datetime-local format to ISO format
 * Adds seconds and timezone to HTML datetime-local input value
 *
 * @param value - Datetime-local string (e.g., "2026-01-27T10:59")
 * @returns ISO string (e.g., "2026-01-27T10:59:00.000Z")
 *
 * @example
 * convertToISOFormat("2026-01-27T10:59") // "2026-01-27T10:59:00.000Z"
 */
export const convertToISOFormat = (value: string): string => {
  if (!value) return '';
  return `${value}:00.000Z`;
};

/**
 * Format a Date/Dayjs value as a backend-friendly "date only" string
 * (no timezone conversion — keeps the day the user actually picked).
 * Matches the naive-datetime format the backend expects, e.g. "2026-07-31T00:00:00".
 */
export const toBackendDateString = (date: Date | Dayjs): string =>
  dayjs(date).format('YYYY-MM-DDT00:00:00');

/**
 * Format a Date/Dayjs value as a backend-friendly "date + time" string
 */
export const toBackendDateTimeString = (
  date: Date | Dayjs,
  dateTimeMode?: GridDateTimeMode
): string => {
  const value = dayjs(date).toDate();

  if (dateTimeMode === GridDateTimeMode.Utc) {
    return toUtcISOStringFromLocalParts(value);
  }

  return value.toISOString();
};

/**
 * Set a date to midnight (start of day) in local time and return as a
 * naive backend-friendly string (no UTC conversion, no day-shift).
 */
export const toIsoStartOfDay = (date: Dayjs): string => toBackendDateString(date);

/**
 * Set a date to the end of day (23:59:59.999) in local time and return as a
 * naive backend-friendly string (no UTC conversion, no day-shift).
 */
export const toIsoEndOfDay = (date: Dayjs): string => dayjs(date).format('YYYY-MM-DDT23:59:59.999');

const toUtcPinnedISOString = (date: Dayjs): string => {
  return toUtcISOStringFromLocalParts(date.toDate());
};

export const toGridFilterStartOfDay = (date: Dayjs, dateTimeMode: GridDateTimeMode): string => {
  const start = date.startOf('day');
  return dateTimeMode === GridDateTimeMode.Utc ? toUtcPinnedISOString(start) : start.toISOString();
};

export const toGridFilterEndOfDay = (date: Dayjs, dateTimeMode: GridDateTimeMode): string => {
  const end = date.endOf('day');
  return dateTimeMode === GridDateTimeMode.Utc ? toUtcPinnedISOString(end) : end.toISOString();
};

export const parseDateSafe = (value: unknown): Date | null => {
  if (typeof value !== 'string') return null;

  try {
    const date = parseISO(value); // strict ISO parsing
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

/** Compare by calendar day in UTC (date-only columns). */
export const toUTCDateOnly = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const MS_PER_MINUTE = 60_000;

/**
 * Compare datetime at minute precision.
 * Uses absolute timestamp so timezone is consistent for both sides.
 */
export const toDateTimeMinute = (date: Date): number => Math.floor(date.getTime() / MS_PER_MINUTE);

export const compareParsedDates = (
  left: Date,
  right: Date,
  op: string,
  mode: 'date' | 'datetime'
): boolean | null => {
  const leftValue = mode === 'datetime' ? toDateTimeMinute(left) : toUTCDateOnly(left);
  const rightValue = mode === 'datetime' ? toDateTimeMinute(right) : toUTCDateOnly(right);

  if (op === '=') return leftValue === rightValue;
  if (op === '<>') return leftValue !== rightValue;
  if (op === '<') return leftValue < rightValue;
  if (op === '<=') return leftValue <= rightValue;
  if (op === '>') return leftValue > rightValue;
  if (op === '>=') return leftValue >= rightValue;
  return null;
};
